/**
 * 4G 1% Risk Position Sizer for Taiwan Equities (TypeScript Port)
 * Strictly matches Python reference implementation in `src/kostolany_egg/scanner/sizer.py`.
 */

import type { PositionSizingParams, SizingResult, StopChoice } from '../types';

export const BROKERAGE_FEE_RATE = 0.001425; // 0.1425%
export const STOCK_TRANSACTION_TAX_RATE = 0.003; // 0.30%
export const ETF_TRANSACTION_TAX_RATE = 0.001; // 0.10%
export const MAX_RISK_FRACTION = 0.01; // 1.0%
export const MAX_POSITION_WEIGHT = 0.1; // 10.0%

/**
 * Returns the Taiwan Stock Exchange (TWSE) official tick size for a given price.
 */
export function getTwseTickSize(price: number): number {
  if (price < 10.0) return 0.01;
  if (price < 50.0) return 0.05;
  if (price < 100.0) return 0.1;
  if (price < 500.0) return 0.5;
  if (price < 1000.0) return 1.0;
  return 5.0;
}

/**
 * Rounds a price to the nearest valid TWSE tick size.
 */
export function roundToTwseTick(
  price: number,
  direction: 'floor' | 'ceil' | 'round' = 'round'
): number {
  if (price <= 0) return 0.0;

  const tick = getTwseTickSize(price);
  let units = price / tick;

  if (direction === 'floor') {
    units = Math.floor(units + 1e-9);
  } else if (direction === 'ceil') {
    units = Math.ceil(units - 1e-9);
  } else {
    units = Math.round(units);
  }

  let res = units * tick;

  // Handle crossing into a new tick band
  const newTick = getTwseTickSize(res);
  if (newTick !== tick) {
    const newUnits = res / newTick;
    res = (direction === 'floor' ? Math.floor(newUnits + 1e-9) : Math.round(newUnits)) * newTick;
  }

  const decimals = res < 500 ? 2 : res < 1000 ? 1 : 0;
  return Number(res.toFixed(decimals));
}

/**
 * Calculates the technical stop-loss price, floored to TWSE tick boundary.
 */
export function calculateStopLossPrice(
  entryPrice: number,
  stopChoice: StopChoice = 'atr',
  atr14?: number | null,
  atrMultiplier = 2.0,
  baseLow?: number | null,
  manualStop?: number | null
): number {
  if (entryPrice <= 0) {
    throw new Error(`Entry price must be positive, got ${entryPrice}`);
  }

  let stop = 0;
  if (stopChoice === 'atr') {
    if (!atr14 || atr14 <= 0) {
      throw new Error(`Valid positive ATR14 is required for 'atr' stop choice, got ${atr14}`);
    }
    stop = entryPrice - atrMultiplier * atr14;
  } else if (stopChoice === 'base_low') {
    if (!baseLow || baseLow <= 0) {
      throw new Error(`Valid positive base low is required for 'base_low' stop choice, got ${baseLow}`);
    }
    stop = baseLow;
  } else if (stopChoice === 'manual') {
    if (!manualStop || manualStop <= 0) {
      throw new Error(`Valid positive manual stop is required for 'manual' stop choice, got ${manualStop}`);
    }
    stop = manualStop;
  } else {
    throw new Error(`Unsupported stop choice: ${stopChoice}`);
  }

  if (stop >= entryPrice) {
    throw new Error(`Calculated stop (${stop}) must be strictly less than entry (${entryPrice})`);
  }
  if (stop <= 0) {
    throw new Error(`Calculated stop (${stop}) must be strictly positive`);
  }

  return roundToTwseTick(stop, 'floor');
}

/**
 * 4G 1% Risk Position Sizer.
 * Enforces:
 * 1. Risk at stop <= 1% equity * macroMultiplier
 * 2. Position value <= 10% equity
 * 3. Max equity cap headroom
 * 4. Full round-trip transaction costs (brokerage buy + sell + tax)
 * 5. Quantities strictly rounded down to lots (1000 shares) and odd-shares
 */
export function sizePosition4G(params: PositionSizingParams): SizingResult {
  const {
    portfolio_equity,
    entry_price,
    stop_loss_price: explicitStop,
    stop_choice = 'atr',
    atr14 = null,
    atr_multiplier = 2.0,
    base_low = null,
    macro_multiplier = 1.0,
    max_equity_cap = 1.0,
    is_etf = false,
  } = params;

  const emptyResult = (reason: string): SizingResult => ({
    shares: 0,
    lots: 0,
    odd_shares: 0,
    entry_price,
    stop_loss_price: 0,
    per_share_loss: 0,
    max_allowed_loss: 0,
    ntd_at_risk: 0,
    position_cost: 0,
    position_value: 0,
    position_weight: 0,
    constraint: 'NONE',
    is_valid: false,
    rejection_reason: reason,
  });

  if (portfolio_equity <= 0) {
    return emptyResult('Portfolio equity must be positive');
  }
  if (entry_price <= 0) {
    return emptyResult('Entry price must be positive');
  }

  let finalStop = explicitStop;
  if (!finalStop || finalStop <= 0) {
    try {
      finalStop = calculateStopLossPrice(
        entry_price,
        stop_choice,
        atr14,
        atr_multiplier,
        base_low
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Invalid stop price';
      return emptyResult(msg);
    }
  }

  if (finalStop >= entry_price) {
    return emptyResult(`Stop loss (${finalStop}) must be strictly below entry price (${entry_price})`);
  }

  // 1. Transaction Costs
  const taxRate = is_etf ? ETF_TRANSACTION_TAX_RATE : STOCK_TRANSACTION_TAX_RATE;
  const buyFeePerShare = entry_price * BROKERAGE_FEE_RATE;
  const sellFeePerShare = finalStop * (BROKERAGE_FEE_RATE + taxRate);
  const perShareLoss = (entry_price - finalStop) + buyFeePerShare + sellFeePerShare;

  if (perShareLoss <= 0) {
    return emptyResult('Per-share loss must be strictly positive');
  }

  // 2. Multipliers and Risk Budget
  const effectiveMultiplier = Math.max(0.0, Math.min(1.0, macro_multiplier));
  if (effectiveMultiplier === 0.0) {
    return {
      shares: 0,
      lots: 0,
      odd_shares: 0,
      entry_price,
      stop_loss_price: finalStop,
      per_share_loss: Number(perShareLoss.toFixed(4)),
      max_allowed_loss: 0,
      ntd_at_risk: 0,
      position_cost: 0,
      position_value: 0,
      position_weight: 0,
      constraint: 'ZERO_MULTIPLIER',
      is_valid: true,
      rejection_reason: 'Macro sizing multiplier is zero',
    };
  }

  const maxAllowedLoss = portfolio_equity * MAX_RISK_FRACTION * effectiveMultiplier;

  // 3. Share limits
  // Constraint A: Risk-based limit
  const sharesRisk = Math.floor(maxAllowedLoss / perShareLoss);

  // Constraint B: 10% weight limit
  const maxPositionValue = portfolio_equity * MAX_POSITION_WEIGHT;
  const costPerShare = entry_price * (1.0 + BROKERAGE_FEE_RATE);
  const sharesWeight = Math.floor(maxPositionValue / costPerShare);

  // Constraint C: Max Equity Cap Headroom
  const maxAllowedCapital = portfolio_equity * Math.max(0.0, Math.min(1.0, max_equity_cap));
  const sharesCap = Math.floor(maxAllowedCapital / costPerShare);

  // Binding constraint
  let finalShares = Math.min(sharesRisk, sharesWeight, sharesCap);
  let constraint: SizingResult['constraint'] = 'NONE';

  if (finalShares === sharesRisk) {
    constraint = 'RISK';
  } else if (finalShares === sharesWeight) {
    constraint = 'WEIGHT';
  } else if (finalShares === sharesCap) {
    constraint = 'CAP_HEADROOM';
  }

  finalShares = Math.max(0, finalShares);

  const lots = Math.floor(finalShares / 1000);
  const oddShares = finalShares % 1000;
  const positionValue = finalShares * entry_price;
  const positionCost = finalShares * costPerShare;
  const ntdAtRisk = finalShares * perShareLoss;
  const positionWeight = portfolio_equity > 0 ? positionValue / portfolio_equity : 0.0;

  return {
    shares: finalShares,
    lots,
    odd_shares: oddShares,
    entry_price,
    stop_loss_price: finalStop,
    per_share_loss: Number(perShareLoss.toFixed(4)),
    max_allowed_loss: Number(maxAllowedLoss.toFixed(2)),
    ntd_at_risk: Number(ntdAtRisk.toFixed(2)),
    position_cost: Number(positionCost.toFixed(2)),
    position_value: Number(positionValue.toFixed(2)),
    position_weight: Number(positionWeight.toFixed(4)),
    constraint,
    is_valid: true,
  };
}
